import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DialogProps } from '@mui/material';

import {
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment as BaggageServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment as VipServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment as MiscServiceFragment,
	AeroexpressTrip,
	AeroexpressSelectedTrip,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	CheckoutOrderFragment,
	SaveOrderServicesMutationVariables,
	CheckinSaveOrderServicesMutationVariables,
	BaggageType
} from '@websky/graphql';
import { Props as ServiceDialogProps } from '../../../Checkout/components/Checkout/OrderPaidServiceCards/components/ServiceCardDialog/ServiceCardDialog';
import { Props as CardExtraModalProps } from './CardExtraModal';
import { Props as PetPopupProps } from '../../../CheckoutAdditionalService/components/PetPopup/PetPopup';
import { Props as InsurancePopupProps } from '../../../InsurancePopup/components/InsurancePopup';
import { Props as VipServiceSelectProps } from '../../../VipServiceSelect';
import { Props as AeroexpressPopupProps } from '../../../CheckoutAdditionalService/components/AeroexpressPopup/AeroexpressPopup';
import { Props as LoyaltyConnectProps } from '../../../Loyalty/components/LoyaltyStub/LoyaltyConnect/LoyaltyConnect';
import { OwnProps as UpgradeFlightClassProps } from '../../../UpgradeFlightClass/components/UpgradeFlightClass/UpgradeFligthClass';
import { Props as SmsPopupProps } from '../../../CheckoutAdditionalService/components/SmsNotifications/components/SmsPopup';
import { ModalXProps } from '../../../BaseComponents';
import { getAeroexpress } from '../../../Checkout/store/order/selectors';
import { getServiceDialogType } from './utils';
import { isServicesSaving } from '../../../Checkout/store/servicesIsSaving/selectors';
import { useAnimalService } from '../../../CheckoutAdditionalService/components/hooks';
import { useSmsNotifications } from '../../../CheckoutAdditionalService/components/SmsNotifications/SmsNotifications.hook';
import {
	concatPassengerNames,
	getAllSegmentsServicePrice,
	getPassengerLabel,
	getPassengerNames,
	useIsMobile
} from '../../../utils';
import { convertMiscSelectorType } from '../../utils';
import PassengerPriceControl from '../../../CheckoutAdditionalService/components/PassengerPriceControl/PassengerPriceControl';
import PassengerPrices from '../../../CheckoutAdditionalService/components/PassengerPrices/PassengerPrices';
import { useCardExtraCtx } from '../CardExtra.context';
import { useCardExtras } from '../../CardExtras.context';
import { SafeHtml } from '@shared/ui';

type SaveOrderServicesVariables = SaveOrderServicesMutationVariables | CheckinSaveOrderServicesMutationVariables;

export type ServiceDialogModel = Pick<
	ServiceDialogProps,
	'order' | 'passengers' | 'passengerLabels' | 'openedModal' | 'onClose'
>;

export type ServicePopupModel =
	| PetPopupProps
	| AeroexpressPopupProps
	| InsurancePopupProps
	| VipServiceSelectProps
	| LoyaltyConnectProps
	| UpgradeFlightClassProps
	| SmsPopupProps;

export function useCardExtraModal({
	openServiceType,
	passengerPrices,
	isDifferentPrices,
	onClose
}: CardExtraModalProps) {
	const { t, i18n } = useTranslation('Checkout');
	const isMobile = useIsMobile();

	const {
		order,
		segments,
		travellers,
		passengers,
		selectedServices,
		type,
		gdsServiceType,
		additionalServicesByType,
		isCanModifyServices,
		onSaveServicesRequest,
		onSaveServices
	} = useCardExtraCtx();

	const { isLoading, onSaveServicesRequest: onSaveOrderServicesRequest } = useCardExtras();

	const aeroexpress = useSelector(getAeroexpress);
	const isOrderServicesSaving = useSelector(isServicesSaving);

	const openServiceDialogType = useMemo(() => getServiceDialogType(openServiceType), [openServiceType]);

	const passengerLabels = useMemo(() => {
		const labelMap: Record<string, string> = {};
		const passengersMap: { [passengerType: string]: number } = {};

		passengers.forEach(passenger => {
			const passengerType = passenger.supplierTravellerType ?? passenger.type;

			if (!passengersMap.hasOwnProperty(passengerType)) {
				passengersMap[passengerType] = 0;
			}

			const passengerIndex = ++passengersMap[passengerType];

			const { firstName, lastName } = getPassengerNames(passenger);

			if (firstName && lastName) {
				labelMap[passenger.id] = concatPassengerNames({ lastName, firstName });
			} else {
				labelMap[passenger.id] = `${getPassengerLabel(passenger)} ${passengerIndex}`;
			}
		});

		return labelMap;
	}, [passengers]);

	const smsTravellers = useMemo(() => travellers.filter(traveller => traveller.type !== 'INF'), [travellers]);

	const serviceDialogModel = useMemo<ServiceDialogModel>(() => {
		return {
			order: order as CheckoutOrderFragment,
			passengers,
			passengerLabels,
			openedModal: openServiceDialogType,
			onClose
		};
	}, [order, passengers, passengerLabels, openServiceDialogType, onClose]);

	const onActionClose = useCallback(
		<T extends any>(request: (...args: T[]) => Promise<void> | void) => async (...args: any[]) => {
			request(...args);
			onClose();
		},
		[onClose]
	);

	let servicePopupModel: ServicePopupModel = null;

	switch (type) {
		case 'animal': {
			const baggageServices = (additionalServicesByType.gdsServices.services as BaggageServiceFragment[]).filter(
				baggage => baggage.baggageType === BaggageType.PetInHandCarrier
			);
			const model = useAnimalService(baggageServices, travellers);

			const petPopupProps: PetPopupProps = {
				segments,
				travellers,
				services: model.servicesByRfisc,
				isLoading: isOrderServicesSaving,
				readOnly: !isCanModifyServices,
				onConfirm: onActionClose(model.onSave),
				onClear: model.onClear,
				onClose
			};

			servicePopupModel = { ...petPopupProps };
			break;
		}
		case 'transfer': {
			const aeroexpressPopupProps: AeroexpressPopupProps = {
				trips: aeroexpress.trips as AeroexpressTrip[],
				selectedTrips: aeroexpress.selectedTrips as AeroexpressSelectedTrip[],
				onSubmit: onSaveServicesRequest,
				onClose
			};

			servicePopupModel = { ...aeroexpressPopupProps };
			break;
		}
		case 'insurance': {
			const insurancePopupProps: InsurancePopupProps = {
				travellers,
				description: additionalServicesByType.insurances?.insurancePrograms?.[0]?.description,
				insurances: additionalServicesByType.insurances?.insurancePrograms,
				selectedInsurances: additionalServicesByType.insurances?.selectedInsurances,
				onSave: onSaveServicesRequest,
				onClose
			};

			servicePopupModel = { ...insurancePopupProps };
			break;
		}
		case 'sms': {
			const smsServices = additionalServicesByType.gdsServices
				.services as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[];
			const { onSubmit } = useSmsNotifications(smsServices, travellers, order.id, onClose);

			const smsPopupProps: SmsPopupProps = {
				smsNotificationId: smsServices[0].id,
				title: i18n.exists('Checkout:SmsPopup_title') ? i18n.t('Checkout:SmsPopup_title') : smsServices[0].name,
				description: i18n.exists('Checkout:SmsPopup_description')
					? i18n.t('Checkout:SmsPopup_description')
					: smsServices[0].vipFullDescription ?? smsServices[0].description,
				travellers: smsTravellers,
				customerValues: order.customer.values,
				price: getAllSegmentsServicePrice(smsServices[0]),
				onClose,
				onSubmit
			};

			servicePopupModel = { ...smsPopupProps };
			break;
		}
		case 'business-lounge':
		case 'priority-boarding':
		case 'baggage-delivery':
		case 'capsule-hotel': {
			const vipServices = additionalServicesByType.gdsServices.services as VipServiceFragment[];

			const headerTitle: Record<string, string> = {
				'business-lounge': t('Business lounge'),
				'priority-boarding': t('Priority boarding'),
				'baggage-delivery': t('Baggage delivery'),
				'capsule-hotel': t('Capsule hotel')
			};

			const controlType: Record<string, 'switch' | 'checkbox' | 'counter'> = {
				'business-lounge': 'checkbox',
				'priority-boarding': 'checkbox',
				'baggage-delivery': 'counter',
				'capsule-hotel': 'checkbox'
			};

			const isCustomControl =
				type === 'business-lounge' || type === 'priority-boarding' || type === 'capsule-hotel';
			const showTotalPrice = type !== 'baggage-delivery';

			const vipServiceSelectProps: VipServiceSelectProps = {
				segments,
				passengers,
				selectedServices,
				header: headerTitle[type] ?? '',
				services: vipServices,
				subheader: (
					<>
						{vipServices[0]?.vipFullDescription && (
							<SafeHtml tag="div" html={vipServices[0].vipFullDescription} />
						)}

						<PassengerPrices prices={passengerPrices} isDifferentPrices={isDifferentPrices} />
					</>
				),
				renderPassenger: isCustomControl
					? ({ passenger, ...props }) => (
							<PassengerPriceControl
								{...props}
								passenger={passenger}
								serviceType={gdsServiceType}
								price={vipServices.find(service => service.id === passenger.serviceId)?.price}
							/>
					  )
					: undefined,
				type: controlType[type],
				showTotalPrice,
				readOnly: !isCanModifyServices,
				onSave: servicesToSave => {
					onSaveServices(servicesToSave);
				},
				onClose
			};

			servicePopupModel = { ...vipServiceSelectProps };
			break;
		}
		case 'misc': {
			const miscServices = additionalServicesByType.gdsServices.services as MiscServiceFragment[];
			const miscService = miscServices[0];

			const vipServiceSelectProps: VipServiceSelectProps = {
				segments,
				passengers,
				selectedServices,
				header: miscService?.name || '',
				subheader:
					miscService?.image || miscService?.fullDescription ? (
						<>
							{miscService?.image && <img src={miscService.image} alt={miscService.name} />}
							{miscService?.fullDescription && (
								<div dangerouslySetInnerHTML={{ __html: miscService.fullDescription }} />
							)}
						</>
					) : null,
				services: miscServices,
				type: convertMiscSelectorType(miscService.selectorType),
				showTotalPrice: true,
				showControlPrice: true,
				readOnly: !isCanModifyServices,
				onSave: servicesToSave => {
					onSaveServices(servicesToSave);
				},
				onClose
			};

			servicePopupModel = { ...vipServiceSelectProps };
			break;
		}
		case 'upgrade': {
			const upgradeFlightClassPopupProps: UpgradeFlightClassProps = {
				loading: isLoading,
				onBackClick: onClose,
				onSaveServices: async ({ params }: SaveOrderServicesVariables) => {
					await onSaveOrderServicesRequest(params);
					onClose();
				}
			};

			servicePopupModel = { ...upgradeFlightClassPopupProps };
			break;
		}
	}

	const { isFullscreenDialog, isNoPadding } = useMemo(() => {
		return {
			isFullscreenDialog: type === 'seats' || type === 'baggage' || type === 'meal',
			isNoPadding: type === 'misc'
		};
	}, [type]);

	const scroll = useMemo<DialogProps['scroll']>(() => {
		if (type === 'animal') {
			return 'body';
		}

		return isMobile ? 'paper' : 'body';
	}, [type, isMobile]);

	const size = useMemo<ModalXProps['size']>(() => {
		switch (type) {
			case 'animal':
				return 'medium';
			case 'baggage-delivery':
			case 'loyalty':
				return 'small';
			case 'transfer':
				return 'large';
			case 'upgrade':
				return 'fullscreen';
			default:
				return 'medium';
		}
	}, [type]);

	return {
		serviceDialogModel,
		servicePopupModel,
		isFullscreenDialog,
		isNoPadding,
		scroll,
		size
	};
}
